/**
 * PixiFactoriesRegistry — single boot point that wires every Pixi*.ts
 * implementation to its corresponding `registerPixi*Factory` slot.
 *
 * Phase H10 Arc λ.1 (2026-05-08): the load-bearing fix that the H9-era
 * audit surfaced. Pre-this-batch, the `registerPixiXxxFactory` setters
 * were called only from unit tests; production code never wired them,
 * meaning `?renderer=pixi` silently fell back to Phaser everywhere
 * (`pixiFactory` stayed `null` in every factory module).
 *
 * After this file is called once at boot:
 *   • Under `?renderer=pixi`, every createXxx factory in
 *     `src/game/rendering/utils/*.ts` returns the Pixi impl.
 *   • Under default (Phaser) mode, the call is a no-op (early-return).
 *
 * The registry intentionally lives in a separate file rather than
 * inside GameBootstrap so it's:
 *   • Discoverable: `grep -r "registerAllPixiFactories"` shows the boot
 *     site immediately.
 *   • Easy to delete in λ.10 once Phaser is gone (this whole file +
 *     the registerPixi*Factory plumbing on each factory module
 *     disappears in the same commit).
 *
 * Architecture: the dispatch chain is
 *   createGraphics() → if (mode==='pixi' && pixiFactory) pixiFactory(...)
 *                                                       └─ wired here
 *                       else → new PhaserGraphics(...)   ← fallback
 *
 * After λ.10, the dispatch hop disappears: createGraphics → new PixiGraphics.
 */

import { getRendererMode } from './RendererFactory';
import { gameLogger } from '../../utils/GameLogger';

import { registerPixiBarLabelFactory } from './utils/BarLabel';
import { registerPixiSpriteFactory } from './utils/Sprite';
import { registerPixiGraphicsFactory } from './utils/Graphics';
import { registerPixiNameTagFactory } from './utils/NameTag';
import { registerPixiPlatformVisualFactory } from './utils/PlatformVisual';
import { registerPixiDebugLabelFactory } from './utils/DebugLabel';
import { registerPixiParticleEmitterFactory } from './utils/ParticleEmitter';
import { registerPixiInteractableFactory } from './utils/InteractableVisual';
import { registerPixiPlateBarFactory } from './plates/PlateBar';
import { registerPixiPipRowFactory } from './overhead/PipRow';
import { registerBakeGraphicsFactory } from '../../game/effects/atmosphere/util/bakeGraphicsToSprite';
import { pixiBakeGraphicsToSprite } from './PixiBakeGraphics';
import { registerPixiFloatingLabelPool } from './utils/FloatingLabel';
import { registerPixiRibbonViewFactory } from './utils/RibbonView';
import { PixiRibbonView } from './PixiRibbonView';
import { acquirePooledFloatingLabel, releasePooledFloatingLabel } from './text/floatingLabelPool';

import { PixiBarLabel } from './PixiBarLabel';
import { PixiSprite } from './PixiSprite';
import { PixiGraphics } from './PixiGraphics';
import { PixiNameTag } from './PixiNameTag';
import { PixiPlatformVisual } from './PixiPlatformVisual';
import { PixiDebugLabel } from './PixiDebugLabel';
import { PixiParticleEmitter } from './PixiParticleEmitter';
import { PixiInteractableVisual } from './PixiInteractableVisual';
import { PixiPlateBar } from './plates/PixiPlateBar';
import { PixiPipRow } from './overhead/PixiPipRow';

let registered = false;

/**
 * Idempotent boot-time wiring of every Pixi*.ts implementation to its
 * factory slot. Skipped under non-Pixi mode (early return). Safe to
 * call multiple times — the `registered` flag prevents double-wiring.
 */
export function registerAllPixiFactories(): void {
  if (getRendererMode() !== 'pixi') return;
  if (registered) return;
  registered = true;

  registerPixiBarLabelFactory((x, y) => new PixiBarLabel(x, y));
  registerPixiSpriteFactory((opts) => new PixiSprite(opts));
  registerPixiGraphicsFactory((style) => new PixiGraphics(style));
  registerPixiNameTagFactory((x, y, text, style) => new PixiNameTag(x, y, text, style));
  registerPixiPlatformVisualFactory((opts) => new PixiPlatformVisual(opts));
  registerPixiDebugLabelFactory(() => new PixiDebugLabel());
  registerPixiParticleEmitterFactory((cfg) => new PixiParticleEmitter(cfg));
  registerPixiInteractableFactory((opts) => new PixiInteractableVisual(opts));
  registerBakeGraphicsFactory(pixiBakeGraphicsToSprite);
  registerPixiPlateBarFactory((style) => new PixiPlateBar(style));
  registerPixiFloatingLabelPool({ acquire: acquirePooledFloatingLabel, release: releasePooledFloatingLabel });
  registerPixiRibbonViewFactory((style) => new PixiRibbonView(style));
  registerPixiPipRowFactory((style) => new PixiPipRow(style));

  gameLogger.info('renderer', '[PixiFactoriesRegistry] All 12 Pixi factories registered.');
}

/**
 * Test-only escape hatch: reset the registered flag so a test can
 * re-run registerAllPixiFactories under a freshly-mocked renderer mode.
 * NEVER call this from production code.
 */
export function __resetPixiFactoriesRegistryForTesting(): void {
  registered = false;
}
